// util to unsubscribe from socket topics
interface Subscription {
  unsubscribe: () => void;
}
export default function unsubscribeFromAll(currentSubscriptions: Subscription[]) {
  if (currentSubscriptions) {
    currentSubscriptions.forEach(subscription => subscription.unsubscribe());
  }
}

/**
 * Capped exponential backoff with jitter for SignalR hub reconnect attempts.
 *
 * Design goals:
 * - Fast first retry to recover from transient blips without user-visible delay.
 * - Exponential growth to avoid hammering an unhealthy server.
 * - Hard cap at 1 hour so long outages still trigger periodic retries.
 * - Jitter to prevent a thundering herd of dispatcher terminals all
 *   reconnecting in lockstep after a backend cycle.
 */
export const RECONNECT_BASE_MS = 2_000;
export const RECONNECT_MAX_MS = 60 * 60 * 1000;

/**
 * Fractional randomness applied to each computed delay, expressed as a
 * deviation from 1.0. With `JITTER_RATIO = 0.2`, the final delay is uniformly
 * distributed in `[raw * 0.8, raw * 1.2]`.
 *
 * Why jitter exists — the thundering herd problem:
 * When many clients disconnect at the same instant (hub restart, load balancer
 * failover, network blip across a plant), pure exponential backoff keeps them
 * synchronized. All N clients retry at t=2s, then all retry at t=4s, then all
 * at t=8s, and so on. Each retry step hits the recovering hub as a load spike
 * rather than steady traffic, and if any single spike exceeds what the hub
 * can service during recovery, the herd repeatedly knocks the hub back over.
 * Exponential growth alone does not fix this because the synchronization is
 * preserved — it just spaces the spikes further apart.
 *
 * Multiplying the delay by a random factor in `[1 - JITTER_RATIO, 1 + JITTER_RATIO]`
 * spreads the retry arrivals across a window that grows with the delay itself,
 * turning a spike into a rolling workload the hub can absorb.
 *
 * Why 0.2 and not 1.0 ("full jitter"):
 * Full jitter maximally decorrelates clients but makes any individual client's
 * retry cadence unpredictable — a user staring at the UI can see retries that
 * fire almost immediately or take twice as long as expected. For a bounded
 * population of dispatcher terminals where per-client UX matters, 0.2 is a
 * pragmatic middle ground: enough spread (±20%) to break synchronization
 * across dozens of clients, small enough that the observed cadence still
 * matches the documented sequence.
 *
 * See https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 * for a comparative analysis of jitter strategies.
 */
const JITTER_RATIO = 0.2;

/**
 * Returns the delay (in milliseconds) to wait before the next reconnect attempt.
 *
 * The raw delay is `RECONNECT_BASE_MS * 2 ** attempt`, clamped to
 * `RECONNECT_MAX_MS`, then scaled by a random factor in
 * `[1 - JITTER_RATIO, 1 + JITTER_RATIO]`.
 *
 * With the default constants (`RECONNECT_BASE_MS=2000`, `RECONNECT_MAX_MS=3_600_000`,
 * `JITTER_RATIO=0.2`), typical delays look like:
 *
 * | attempt | raw delay      | jittered range (±20%)  |
 * | ------- | -------------- | ---------------------- |
 * | 0       | 2 s            | 1.6 s – 2.4 s          |
 * | 1       | 4 s            | 3.2 s – 4.8 s          |
 * | 2       | 8 s            | 6.4 s – 9.6 s          |
 * | 3       | 16 s           | 12.8 s – 19.2 s        |
 * | 4       | 32 s           | 25.6 s – 38.4 s        |
 * | 5       | 64 s (~1 m)    | ~51 s – ~77 s          |
 * | 6       | 128 s (~2 m)   | ~1 m 42 s – ~2 m 34 s  |
 * | 7       | 256 s (~4 m)   | ~3 m 25 s – ~5 m 7 s   |
 * | 8       | 512 s (~8 m)   | ~6 m 50 s – ~10 m 14 s |
 * | 9       | 1024 s (~17 m) | ~13 m 39 s – ~20 m 29 s|
 * | 10      | 2048 s (~34 m) | ~27 m 18 s – ~40 m 58 s|
 * | 11+     | 3600 s (1 h)   | ~48 m – ~72 m (capped) |
 *
 * Cumulative time to reach the 1 h cap is roughly 68 minutes, after which
 * every attempt fires within about an hour of the previous one.
 *
 * @param attempt Zero-based retry index. The first reconnect after a drop
 *                should pass 0, the second should pass 1, and so on. The
 *                counter should be reset to 0 once a connection is fully
 *                re-established (handshake + post-connect group joins).
 * @returns A delay in milliseconds, capped at {@link RECONNECT_MAX_MS}, with
 *          +/- {@link JITTER_RATIO} random jitter applied.
 */
export function computeReconnectDelayMs(attempt: number): number {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  // Clamp the exponent so Math.pow doesn't produce Infinity on pathological input.
  const exponent = Math.min(safeAttempt, 40);
  const exponential = RECONNECT_BASE_MS * 2 ** exponent;
  const bounded = Math.min(exponential, RECONNECT_MAX_MS);
  const jitter = 1 + (Math.random() * 2 - 1) * JITTER_RATIO;
  return Math.max(0, Math.round(bounded * jitter));
}

/**
 * Owns the "when do we retry a SignalR hub connection" state machine shared
 * by every socket service in this app: the attempt counter that drives
 * {@link computeReconnectDelayMs}, the pending {@link setTimeout} handle, and
 * the invariants that protect against overlapping or leaked retries.
 *
 * Held by composition (not inheritance) so each socket keeps its own
 * connect()/disconnect() body intact — the differences between sockets
 * dominate the similarities, and a base class would force awkward abstraction
 * over parameter lists, group-join strategies, and post-connect side effects
 * that are legitimately per-socket.
 *
 * Invariants:
 * - Only one reconnect timer exists at a time. {@link schedule} cancels any
 *   pending timer before queuing a new one.
 * - Attempt counter only advances through {@link schedule}; it is cleared
 *   by {@link reset}, which also cancels any pending timer.
 */
export class ReconnectScheduler {
  private attempt = 0;

  private timer?: ReturnType<typeof setTimeout>;

  /**
   * Queues `run` after a capped exponential backoff delay. Cancels any
   * previously scheduled attempt first so concurrent reconnects can't
   * stack up (e.g. if both onclose and the connect() catch path fire
   * back-to-back during a flapping server).
   *
   * @param run Callback that performs the actual reconnect work. Typically
   *            invokes the owning socket's connect() with the right args.
   */
  schedule(run: () => void): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    const delay = computeReconnectDelayMs(this.attempt);
    this.attempt += 1;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      run();
    }, delay);
  }

  /**
   * Clears the attempt counter so the next {@link schedule} starts at the
   * minimum delay again. Call after a fully successful reconnect (handshake
   * plus all post-connect group joins) so a later drop recovers quickly
   * instead of inheriting stale delay from an earlier outage. Also cancels
   * any pending timer, so it is safe to call as part of a full teardown.
   */
  reset(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.attempt = 0;
  }

  /**
   * True if a reconnect is currently queued. Exposed for tests and for
   * callers that need to know whether they are the first to initiate
   * recovery from a given close.
   */
  get isPending(): boolean {
    return this.timer !== undefined;
  }

  /**
   * Current zero-based attempt count. Exposed for diagnostics (e.g. logging
   * or metrics on how deep into the backoff we are).
   */
  get currentAttempt(): number {
    return this.attempt;
  }
}

/**
 * True when a SignalR connect/negotiate failure was a 401/403 auth rejection
 * rather than a transport problem -- this gates the retry-vs-logout decision.
 * Prefers the HttpError statusCode; falls back to the message, which SignalR
 * formats as "<reason>: Status code '<code>'". The pattern is anchored to the
 * end so an error merely quoting such text mid-message isn't misclassified.
 */
export function isAuthFailureError(err: unknown): boolean {
  const statusCode = (err as { statusCode?: unknown })?.statusCode;
  if (typeof statusCode === 'number') {
    return statusCode === 401 || statusCode === 403;
  }
  const message = err instanceof Error ? err.message : String(err);
  return /status code '40[13]'\.?\s*$/i.test(message);
}

export type ConnectionState =
  | 'Disconnected'
  | 'Connecting'
  | 'Connected'
  | 'Disconnecting'
  | 'Reconnecting';
export const ConnectionState: Record<ConnectionState, ConnectionState> = {
  Disconnected: 'Disconnected',
  Disconnecting: 'Disconnecting',
  Reconnecting: 'Reconnecting',
  Connecting: 'Connecting',
  Connected: 'Connected',
} as const;
